import type { AuditCategory, AuditOutcome, AuditQuery } from "@/lib/api";
import { isAuditCategory } from "./audit-copy.ts";

// The Audit log page's URL: every filter is a search param, so a link such as
// /orgs/<org>/audit?category=access opens the page filtered.

export const AUDIT_RANGES = ["24h", "7d", "30d", "90d", "custom"] as const;
export type AuditRange = (typeof AUDIT_RANGES)[number];

export const RANGE_LABEL: Record<AuditRange | "all", string> = {
  all: "All time",
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  custom: "Custom range",
};

const RANGE_MS: Record<Exclude<AuditRange, "custom">, number> = {
  "24h": 86_400_000,
  "7d": 7 * 86_400_000,
  "30d": 30 * 86_400_000,
  "90d": 90 * 86_400_000,
};

// "Now" for the preset ranges, rounded to the minute so the query key holds still between renders.
export function currentMinute(): number {
  return Math.floor(Date.now() / 60_000) * 60_000;
}

// The export streams at most this many days.
export const EXPORT_MAX_DAYS = 366;

export type AuditSearch = {
  category?: AuditCategory;
  boo?: string;
  // The actor's id: a member's userId.
  person?: string;
  outcome?: AuditOutcome;
  range?: AuditRange;
  // YYYY-MM-DD, local days, with range=custom.
  from?: string;
  to?: string;
  // An exact catalog key or a `prefix.*`, from deep links.
  action?: string;
  // Super admins: include Boo Lab's internal events.
  internal?: true;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const OUTCOMES: AuditOutcome[] = ["success", "denied", "error"];

const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);

export function parseAuditSearch(search: Record<string, unknown>): AuditSearch {
  const out: AuditSearch = {};
  if (isAuditCategory(search.category)) out.category = search.category;
  const boo = str(search.boo);
  if (boo) out.boo = boo;
  const person = str(search.person);
  if (person) out.person = person;
  if (OUTCOMES.includes(search.outcome as AuditOutcome)) out.outcome = search.outcome as AuditOutcome;
  if (AUDIT_RANGES.includes(search.range as AuditRange)) out.range = search.range as AuditRange;
  const from = str(search.from);
  const to = str(search.to);
  if (from && DAY.test(from)) out.from = from;
  if (to && DAY.test(to)) out.to = to;
  // Dates alone mean a custom range.
  if ((out.from || out.to) && !out.range) out.range = "custom";
  if (out.range !== "custom") {
    delete out.from;
    delete out.to;
  }
  const action = str(search.action);
  if (action) out.action = action;
  if (search.internal === true || search.internal === "1" || search.internal === 1) out.internal = true;
  return out;
}

// Local midnight of a YYYY-MM-DD day.
function dayStart(day: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** The time window the filters ask for, as ISO strings; `now` is rounded by the caller so query keys stay put. */
function auditWindow(s: AuditSearch, now: number): { from?: string; to?: string } {
  if (!s.range) return {};
  if (s.range === "custom") {
    const to = s.to ? dayStart(s.to) : null;
    if (to) to.setDate(to.getDate() + 1);
    return { from: s.from ? dayStart(s.from).toISOString() : undefined, to: to?.toISOString() };
  }
  return { from: new Date(now - RANGE_MS[s.range]).toISOString() };
}

export function auditQueryOf(s: AuditSearch, now: number): AuditQuery {
  return {
    category: s.category,
    booId: s.boo,
    actor: s.person,
    outcome: s.outcome,
    action: s.action,
    includeInternal: s.internal,
    ...auditWindow(s, now),
  };
}

/**
 * The export's window: the page's, trimmed to the last EXPORT_MAX_DAYS before
 * its end. `trimmed` says the page shows more than the file will hold.
 */
export function exportWindow(q: AuditQuery, now: number): { from: string; to?: string; trimmed: boolean } {
  const end = q.to ? Date.parse(q.to) : now;
  const earliest = end - EXPORT_MAX_DAYS * 86_400_000;
  const from = q.from ? Date.parse(q.from) : null;
  if (from !== null && from >= earliest) return { from: q.from!, to: q.to, trimmed: false };
  return { from: new Date(earliest).toISOString(), to: q.to, trimmed: true };
}

export function hasAuditFilters(s: AuditSearch): boolean {
  return !!(s.category || s.boo || s.person || s.outcome || s.range || s.action);
}
